import * as React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  Building2,
  LayoutGrid,
  Megaphone,
  Target,
  Users,
  Wallet,
} from 'lucide-react';
import { type ListingListResponse } from '@proptr/shared';
import type { Feed } from '@proptr/api-client';
import { DEFAULT_BRAND, ListingBanner } from '@proptr/creative-templates';
import { ListingPickerModal } from '../components/ListingPickerModal.js';
import { SectionCard } from '../components/SectionCard.js';
import { AppShell } from '../components/layout/AppShell.js';
import { Button } from '../components/ui/button.js';
import { Card } from '../components/ui/card.js';
import { Input } from '../components/ui/input.js';
import { Label } from '../components/ui/label.js';
import { MultiSelect } from '../components/ui/multi-select.js';
import { Select, type SelectOption } from '../components/ui/select.js';
import { bannerListingFrom } from '../lib/bannerListing.js';
import { cn } from '../lib/utils.js';
import { client } from '../main.js';

// Paid campaigns on Meta. Posts and campaigns share a shape — pick a
// listing, pick where it goes — and diverge on everything that costs
// money: an ad account, an objective, an audience, a budget, and the
// legal category the ad runs under.
//
// Meta-only by design. Google and TikTok have different objective
// models and different audience rules, and a builder that pretended
// otherwise would be lying in three places at once.

type ListingRow = ListingListResponse['items'][number];

interface MetaPage {
  id: string;
  name: string;
  picture: string | null;
  instagram: { id: string; username: string | null; picture: string | null } | null;
}

interface MetaAdAccount {
  id: string;
  accountId: string;
  name: string;
  currency: string | null;
  active: boolean;
}

/**
 * Meta's outcome-driven objectives, narrowed to the three that make
 * sense for a listing.
 *
 * The full enum has six. Awareness and App Promotion are not what a
 * brokerage advertising a property wants, and offering them would mean
 * building bidding and destination logic nobody would use.
 */
const OBJECTIVES: {
  id: string;
  label: string;
  description: string;
}[] = [
  {
    id: 'OUTCOME_TRAFFIC',
    label: 'Traffic',
    description: 'Send people to the listing on your proptree.',
  },
  {
    id: 'OUTCOME_LEADS',
    label: 'Leads',
    description: 'Collect enquiries with a form, without leaving Meta.',
  },
  {
    id: 'OUTCOME_ENGAGEMENT',
    label: 'Engagement',
    description: 'Messages and interactions on the ad itself.',
  },
];

/**
 * Where HOUSING rules bite.
 *
 * Meta requires the housing special ad category for ads targeting the
 * US (since 2019), Canada (2020) and Europe (2021). The UAE is not on
 * that list, which is why this is a per-campaign decision rather than a
 * property of the product: a Dubai brokerage advertising to Dubai runs
 * unrestricted, and the same brokerage advertising to Sweden does not.
 */
const HOUSING_COUNTRIES = new Set([
  'US', 'CA',
  // EU/EEA + UK.
  'AT', 'BE', 'BG', 'HR', 'CY', 'CZ', 'DK', 'EE', 'FI', 'FR', 'DE', 'GR',
  'HU', 'IE', 'IT', 'LV', 'LT', 'LU', 'MT', 'NL', 'PL', 'PT', 'RO', 'SK',
  'SI', 'ES', 'SE', 'IS', 'LI', 'NO', 'GB',
]);

const COUNTRIES: SelectOption<string>[] = [
  { value: 'AE', label: 'United Arab Emirates' },
  { value: 'SA', label: 'Saudi Arabia' },
  { value: 'GB', label: 'United Kingdom' },
  { value: 'SE', label: 'Sweden' },
  { value: 'DE', label: 'Germany' },
  { value: 'FR', label: 'France' },
  { value: 'US', label: 'United States' },
  { value: 'IN', label: 'India' },
  { value: 'PK', label: 'Pakistan' },
  { value: 'RU', label: 'Russia' },
];

/**
 * What the campaign promotes. This is the first decision because every
 * later one hangs off it: the destination URL, which creative gets
 * rendered, and how many ads the campaign actually contains.
 *
 *   agency-proptree — one ad, pointing at the brokerage's own page
 *   agent-proptrees — one ad per agent, each to that agent's page
 *   listings        — one ad per listing, to the listing on the proptree
 */
type CampaignKind = 'agency-proptree' | 'agent-proptrees' | 'listings';

const CAMPAIGN_KINDS: SelectOption<CampaignKind>[] = [
  { value: 'agency-proptree', label: 'Boost agency proptree' },
  { value: 'agent-proptrees', label: 'Boost agent proptrees' },
  { value: 'listings', label: 'Boost listings' },
];

const KIND_HINT: Record<CampaignKind, string> = {
  'agency-proptree':
    "One ad for the brokerage's own page — everything it lists, under one link.",
  'agent-proptrees':
    "One ad per agent, each pointing at that agent's own page.",
  listings: 'One ad per listing, pointing at that listing on your proptree.',
};

/**
 * Where a listings campaign gets its listings.
 *
 * A feed is a live query — the campaign keeps advertising whatever
 * currently matches, so new stock enters and sold stock leaves without
 * anyone editing the campaign. A hand-picked set is frozen, which is
 * what you want for a launch or a single trophy property.
 */
type ListingSource = 'feed' | 'specific';

const LISTING_SOURCES: SelectOption<ListingSource>[] = [
  { value: 'feed', label: 'A feed' },
  { value: 'specific', label: 'Specific listings' },
];

const BUDGET_KINDS: SelectOption<'daily' | 'lifetime'>[] = [
  { value: 'daily', label: 'Daily budget' },
  { value: 'lifetime', label: 'Total budget' },
];

/** Rendered creative width. The column is 480 and the card pads 20 a
 *  side, so this fills it without the banner deciding its own size. */
const PREVIEW_WIDTH = 440;

/** Meta's floor for housing ads. Below this the ad set is rejected. */
const HOUSING_MIN_RADIUS_KM = 25;

interface CampaignForm {
  kind: CampaignKind;
  /** Only meaningful when kind === 'listings'. */
  listingSource: ListingSource;
  feedId: string | null;
  listingIds: string[];
  name: string;
  adAccountId: string | null;
  pageId: string | null;
  instagramId: string | null;
  objective: string;
  country: string;
  radiusKm: number;
  ageMin: number;
  ageMax: number;
  budgetKind: 'daily' | 'lifetime';
  budget: string;
  startAt: string;
  endAt: string;
}

const EMPTY_FORM: CampaignForm = {
  // Listings is the one with a creative pipeline behind it today, so it
  // is where an empty form should land.
  kind: 'listings',
  // A feed by default: it keeps working as stock turns over, where a
  // hand-picked set quietly goes stale.
  listingSource: 'feed',
  feedId: null,
  listingIds: [],
  name: '',
  adAccountId: null,
  pageId: null,
  instagramId: null,
  objective: 'OUTCOME_TRAFFIC',
  country: 'AE',
  radiusKm: 25,
  ageMin: 18,
  ageMax: 65,
  budgetKind: 'daily',
  budget: '100',
  startAt: '',
  endAt: '',
};

export function CampaignEditPage() {
  const navigate = useNavigate();
  const [form, setForm] = React.useState<CampaignForm>(EMPTY_FORM);
  const set = <K extends keyof CampaignForm>(k: K, v: CampaignForm[K]) =>
    setForm((f) => ({ ...f, [k]: v }));

  const [listings, setListings] = React.useState<ListingRow[] | null>(null);
  const [pages, setPages] = React.useState<MetaPage[] | null>(null);
  const [adAccounts, setAdAccounts] = React.useState<MetaAdAccount[] | null>(
    null,
  );
  const [connected, setConnected] = React.useState<boolean | null>(null);

  const [feeds, setFeeds] = React.useState<Feed[] | null>(null);
  const [pickerOpen, setPickerOpen] = React.useState(false);

  React.useEffect(() => {
    let alive = true;
    void client.feeds
      .list()
      .then((res) => {
        if (!alive) return;
        setFeeds(res.items);
        // Default to one that can actually run: the first Meta feed
        // already published to a catalog.
        const usable = res.items.find(
          (x) => x.format === 'meta' && x.meta?.catalogId,
        );
        setForm((f) => (f.feedId ? f : { ...f, feedId: usable?._id ?? null }));
      })
      .catch(() => alive && setFeeds([]));
    return () => {
      alive = false;
    };
  }, []);

  React.useEffect(() => {
    let alive = true;
    void client.listings
      .list({ status: 'active', type: 'all', page: 1, limit: 200 })
      .then((res) => {
        if (!alive) return;
        setListings(res.items);
      })
      .catch(() => alive && setListings([]));
    return () => {
      alive = false;
    };
  }, []);

  // Pages and ad accounts both come from the Meta connection, so one
  // failure mode covers both: a 409 means "not connected", which the UI
  // has to distinguish from "connected but nothing shared".
  React.useEffect(() => {
    let alive = true;
    void (async () => {
      const load = async <T,>(path: string): Promise<[T[], boolean]> => {
        const res = await fetch(path, { credentials: 'include' });
        if (res.status === 409) return [[], false];
        if (!res.ok) return [[], true];
        return [((await res.json()) as { items: T[] }).items, true];
      };
      try {
        const [[p, ok], [a]] = await Promise.all([
          load<MetaPage>('/api/auth/meta/pages'),
          load<MetaAdAccount>('/api/auth/meta/ad-accounts'),
        ]);
        if (!alive) return;
        setPages(p);
        setAdAccounts(a);
        setConnected(ok);
        setForm((f) => ({
          ...f,
          adAccountId: f.adAccountId ?? (a.length === 1 ? a[0]!.id : null),
          pageId: f.pageId ?? (p.length === 1 ? p[0]!.id : null),
          instagramId:
            f.instagramId ?? (p.length === 1 ? (p[0]!.instagram?.id ?? null) : null),
        }));
      } catch {
        if (alive) {
          setPages([]);
          setAdAccounts([]);
          setConnected(true);
        }
      }
    })();
    return () => {
      alive = false;
    };
  }, []);

  const selectedPage = pages?.find((p) => p.id === form.pageId) ?? null;
  // Only home_listings feeds, and only those actually published to a
  // Meta catalog. A WhatsApp/products feed can't back a Facebook or
  // Instagram ad, and an unpublished feed has no catalog for the ad set
  // to reference — both would fail at launch rather than here.
  const metaFeeds = (feeds ?? []).filter((f) => f.format === 'meta');
  const publishedMetaFeeds = metaFeeds.filter((f) => f.meta?.catalogId);
  const selectedFeed = feeds?.find((f) => f._id === form.feedId) ?? null;
  // What the preview shows. A feed campaign has no single creative, so it
  // previews the first listing the feed matches — representative, and
  // honest about being one of many.
  const previewListing =
    form.kind !== 'listings'
      ? null
      : form.listingSource === 'specific'
        ? (listings?.find((l) => l._id === form.listingIds[0]) ?? null)
        : (listings?.[0] ?? null);
  const adCount =
    form.kind !== 'listings'
      ? null
      : form.listingSource === 'feed'
        ? (selectedFeed?.listingCount ?? null)
        : form.listingIds.length;

  // The whole audience section keys off this. Meta rejects a housing ad
  // that targets by age, by gender, or inside a 25km radius, so those
  // controls are locked rather than merely warned about — a control that
  // can be set to a value the platform will refuse is worse than no
  // control.
  const housing = HOUSING_COUNTRIES.has(form.country);
  React.useEffect(() => {
    if (!housing) return;
    setForm((f) => ({
      ...f,
      ageMin: 18,
      ageMax: 65,
      radiusKm: Math.max(f.radiusKm, HOUSING_MIN_RADIUS_KM),
    }));
  }, [housing]);

  const problems: string[] = [];
  if (!form.name.trim()) problems.push('Give the campaign a name.');
  if (!form.adAccountId) problems.push('Pick an ad account.');
  if (!form.pageId) problems.push('Pick a Facebook page.');
  if (form.kind === 'listings') {
    if (form.listingSource === 'feed') {
      if (!form.feedId) problems.push('Pick a feed.');
      else if (!selectedFeed?.meta?.catalogId) {
        problems.push('That feed isn\'t published to a Meta catalog yet.');
      } else if (selectedFeed?.listingCount === 0) {
        // A feed matching nothing makes a campaign with no ads in it —
        // Meta accepts the spend and shows nobody anything.
        problems.push('That feed matches no listings right now.');
      }
    } else if (form.listingIds.length === 0) {
      problems.push('Pick at least one listing.');
    }
  }
  if (!(Number(form.budget) > 0)) problems.push('Set a budget above zero.');

  const currency =
    adAccounts?.find((a) => a.id === form.adAccountId)?.currency ?? '';

  return (
    <AppShell>
      <ListingPickerModal
        open={pickerOpen}
        listings={listings}
        value={form.listingIds}
        onClose={() => setPickerOpen(false)}
        onConfirm={(next) => {
          set('listingIds', next);
          setPickerOpen(false);
        }}
      />
      <div className="space-y-6 pb-10">
        <div className="min-w-0">
          <h1 className="font-display text-[40px] font-medium leading-none tracking-[-0.02em]">
            New campaign
          </h1>
          <div className="mt-3 flex items-center gap-3">
            <Link
              to="/marketing"
              className="inline-flex items-center gap-1.5 font-mono text-[11px] text-muted-foreground hover:text-foreground"
            >
              <ArrowLeft className="h-3.5 w-3.5" />
              Marketing
            </Link>
            <span className="font-mono text-[11px] text-muted-foreground">
              Meta — Facebook and Instagram
            </span>
          </div>
        </div>

        {connected === false && (
          <Card className="border-destructive/40 p-4">
            <p className="font-sans text-[13px] text-muted-foreground">
              Meta isn't connected, so there are no ad accounts or pages to
              run this on. Connect it from{' '}
              <Link to="/marketing" className="text-primary hover:underline">
                Marketing
              </Link>
              .
            </p>
          </Card>
        )}

        {/* 480px, not 380: the preview is a square creative, so the column
            width IS the creative's size. At 380 the ad was the smallest
            thing on a page about that ad. */}
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-[minmax(0,1fr)_480px]">
          <div className="space-y-6">
            {/* Account before Campaign: which ad account pays and which
                page it runs from are properties of the connection, not of
                this campaign — they're the same for every campaign the
                brokerage makes. Asking them first also means the campaign
                section is only about the campaign. */}
            <SectionCard
              title="Account"
              subtitle="Who pays, and where the ad appears from"
              icon={Building2}
              collapsible={false}
            >
              <div className="grid grid-cols-12 gap-4 p-5">
                <div className="col-span-12 sm:col-span-4">
                  <Label>Ad account</Label>
                  <Select
                    className="mt-1 bg-muted/30"
                    options={(adAccounts ?? []).map((a) => ({
                      value: a.id,
                      label: a.currency ? `${a.name} · ${a.currency}` : a.name,
                      // An inactive account can't spend; showing it as
                      // selectable would fail at launch instead of here.
                      disabled: !a.active,
                      badge: a.active ? undefined : 'Inactive',
                    }))}
                    value={form.adAccountId ?? ''}
                    onChange={(v) => set('adAccountId', v)}
                    placeholder={
                      adAccounts === null
                        ? 'Loading…'
                        : adAccounts.length === 0
                          ? 'None shared'
                          : 'Select an account'
                    }
                  />
                </div>
                <div className="col-span-12 sm:col-span-4">
                  <Label>Facebook page</Label>
                  <Select
                    className="mt-1 bg-muted/30"
                    options={(pages ?? []).map((p) => ({
                      value: p.id,
                      label: p.name,
                      image: p.picture,
                    }))}
                    value={form.pageId ?? ''}
                    onChange={(v) => {
                      const page = pages?.find((p) => p.id === v) ?? null;
                      setForm((f) => ({
                        ...f,
                        pageId: v,
                        instagramId: page?.instagram?.id ?? null,
                      }));
                    }}
                    placeholder={
                      pages === null
                        ? 'Loading…'
                        : pages.length === 0
                          ? 'None shared'
                          : 'Select a page'
                    }
                  />
                </div>
                <div className="col-span-12 sm:col-span-4">
                  <Label>Instagram account</Label>
                  <Select
                    className="mt-1 bg-muted/30"
                    options={
                      selectedPage?.instagram
                        ? [
                            {
                              value: selectedPage.instagram.id,
                              label: selectedPage.instagram.username
                                ? `@${selectedPage.instagram.username}`
                                : selectedPage.instagram.id,
                              image: selectedPage.instagram.picture,
                            },
                          ]
                        : []
                    }
                    value={form.instagramId ?? ''}
                    onChange={(v) => set('instagramId', v)}
                    placeholder={
                      !form.pageId
                        ? 'Pick a page first'
                        : selectedPage?.instagram
                          ? 'Select an account'
                          : 'None linked'
                    }
                  />
                </div>
                {(adAccounts?.length ?? 0) === 0 && connected && (
                  <div className="col-span-12">
                    <p className="font-mono text-[10px] leading-relaxed text-muted-foreground">
                      Meta is connected but shared no ad accounts. Add Ad
                      accounts to the app's login configuration, then
                      reconnect.
                    </p>
                  </div>
                )}
              </div>
            </SectionCard>

            <SectionCard
              title="Campaign"
              subtitle="What you're promoting"
              icon={Megaphone}
              collapsible={false}
            >
              <div className="grid grid-cols-12 gap-4 p-5">
                <div className="col-span-12">
                  <Label htmlFor="campaign-name">Campaign name</Label>
                  <Input
                    id="campaign-name"
                    className="mt-1"
                    value={form.name}
                    onChange={(e) => set('name', e.target.value)}
                    placeholder="Marina Gate 1 — March"
                  />
                </div>
                <div className="col-span-12 sm:col-span-6">
                  <Label>Campaign type</Label>
                  <Select
                    className="mt-1 bg-muted/30"
                    options={CAMPAIGN_KINDS}
                    value={form.kind}
                    onChange={(v) => set('kind', v)}
                    searchable={false}
                  />
                  <p className="mt-1.5 font-mono text-[10px] leading-relaxed text-muted-foreground">
                    {KIND_HINT[form.kind]}
                  </p>
                </div>
                {form.kind === 'listings' && (
                  <>
                    <div className="col-span-12 sm:col-span-6">
                      <Label>Listings from</Label>
                      <Select
                        className="mt-1 bg-muted/30"
                        options={LISTING_SOURCES}
                        value={form.listingSource}
                        onChange={(v) => set('listingSource', v)}
                        searchable={false}
                      />
                    </div>
                    {form.listingSource === 'feed' ? (
                      <div className="col-span-12">
                        <Label>Feed</Label>
                        <Select
                          className="mt-1 bg-muted/30"
                          options={metaFeeds.map((f) => ({
                            value: f._id,
                            label: f.name || 'Untitled feed',
                            // The tag IS the gate: an ad set points at a
                            // Meta catalog, and a feed we haven't
                            // published to one has nothing to point at.
                            badge: f.meta?.catalogId
                              ? 'In catalog'
                              : 'Not published',
                            badgeTone: f.meta?.catalogId
                              ? ('success' as const)
                              : ('default' as const),
                            disabled: !f.meta?.catalogId,
                          }))}
                          value={form.feedId ?? ''}
                          onChange={(v) => set('feedId', v)}
                          placeholder={
                            feeds === null
                              ? 'Loading…'
                              : metaFeeds.length === 0
                                ? 'No Meta feeds yet'
                                : 'Select a feed'
                          }
                        />
                        <p className="mt-1.5 font-mono text-[10px] leading-relaxed text-muted-foreground">
                          {metaFeeds.length === 0
                            ? 'Create a Meta catalog feed first — WhatsApp feeds cannot back a Facebook or Instagram ad.'
                            : publishedMetaFeeds.length === 0
                              ? 'None of your Meta feeds are published to a catalog yet. Publish one from Marketing.'
                              : `The campaign follows the feed — listings that start matching are added, sold ones drop out.${
                                  selectedFeed?.listingCount !== undefined
                                    ? ` ${selectedFeed.listingCount} today.`
                                    : ''
                                }`}
                        </p>
                      </div>
                    ) : (
                      <div className="col-span-12">
                        <Label>Listings</Label>
                        {/* Dropdown and browser edit the same value. The
                            dropdown is faster when you know the name; the
                            browser is the only workable option when you
                            are choosing by looking at the property. */}
                        <div className="mt-1 flex items-center gap-2">
                          <MultiSelect
                            className="flex-1 bg-muted/30"
                            options={(listings ?? []).map((l) => ({
                              value: l._id,
                              label: l.title || l.externalId || l._id,
                            }))}
                            value={form.listingIds}
                            onChange={(v) => set('listingIds', v)}
                            allLabel="Pick listings"
                            itemNoun="listings"
                          />
                          <Button
                            type="button"
                            variant="outline"
                            className="shrink-0"
                            onClick={() => setPickerOpen(true)}
                          >
                            <LayoutGrid className="h-3.5 w-3.5" />
                            Browse
                          </Button>
                        </div>
                        <p className="mt-1.5 font-mono text-[10px] leading-relaxed text-muted-foreground">
                          Fixed set — this campaign keeps running these
                          listings even after they sell.
                        </p>
                      </div>
                    )}
                  </>
                )}
              </div>
            </SectionCard>

            <SectionCard
              title="Objective"
              subtitle="What Meta optimises the spend towards"
              icon={Target}
              collapsible={false}
            >
              <div
                role="radiogroup"
                aria-label="Campaign objective"
                className="grid grid-cols-1 gap-2 p-5 sm:grid-cols-3"
              >
                {OBJECTIVES.map((o) => {
                  const active = form.objective === o.id;
                  return (
                    <button
                      key={o.id}
                      type="button"
                      role="radio"
                      aria-checked={active}
                      onClick={() => set('objective', o.id)}
                      className={cn(
                        'rounded-xl border p-4 text-left transition-colors',
                        active
                          ? 'border-primary bg-primary/5 ring-2 ring-primary/25'
                          : 'border-border hover:border-primary/40 hover:bg-muted dark:border-white/10',
                      )}
                    >
                      <div className="font-sans text-[14px] font-semibold leading-tight">
                        {o.label}
                      </div>
                      <p className="mt-1 font-sans text-[12px] leading-snug text-muted-foreground">
                        {o.description}
                      </p>
                    </button>
                  );
                })}
              </div>
            </SectionCard>

            <SectionCard
              title="Audience"
              subtitle="Who sees it"
              icon={Users}
              collapsible={false}
            >
              <div className="grid grid-cols-12 gap-4 p-5">
                <div className="col-span-12 sm:col-span-6">
                  <Label>Country</Label>
                  <Select
                    className="mt-1 bg-muted/30"
                    options={COUNTRIES}
                    value={form.country}
                    onChange={(v) => set('country', v)}
                  />
                </div>
                <div className="col-span-12 sm:col-span-6">
                  <Label htmlFor="campaign-radius">Radius (km)</Label>
                  <Input
                    id="campaign-radius"
                    className="mt-1"
                    type="number"
                    min={housing ? HOUSING_MIN_RADIUS_KM : 1}
                    value={String(form.radiusKm)}
                    onChange={(e) =>
                      set('radiusKm', Number(e.target.value) || 0)
                    }
                  />
                </div>

                {housing && (
                  // Not a warning to be dismissed — the fields below are
                  // locked because Meta will reject the ad set otherwise,
                  // and finding that out at launch is worse than reading
                  // it here.
                  <div className="col-span-12 rounded-xl border border-border bg-muted/40 p-4">
                    <div className="mono-label text-foreground">
                      Housing ad rules apply
                    </div>
                    <p className="mt-2 font-sans text-[12px] leading-snug text-muted-foreground">
                      Meta treats property ads in this country as a special
                      ad category. Age and gender are fixed, detailed
                      interest targeting and lookalikes are unavailable, and
                      the radius can't go below {HOUSING_MIN_RADIUS_KM}km.
                      These aren't our limits — the campaign is rejected
                      without them.
                    </p>
                  </div>
                )}

                <div className="col-span-12 sm:col-span-6">
                  <Label htmlFor="campaign-age-min">Age from</Label>
                  <Input
                    id="campaign-age-min"
                    className="mt-1"
                    type="number"
                    min={18}
                    max={65}
                    disabled={housing}
                    value={String(form.ageMin)}
                    onChange={(e) => set('ageMin', Number(e.target.value) || 18)}
                  />
                </div>
                <div className="col-span-12 sm:col-span-6">
                  <Label htmlFor="campaign-age-max">Age to</Label>
                  <Input
                    id="campaign-age-max"
                    className="mt-1"
                    type="number"
                    min={18}
                    max={65}
                    disabled={housing}
                    value={String(form.ageMax)}
                    onChange={(e) => set('ageMax', Number(e.target.value) || 65)}
                  />
                </div>
              </div>
            </SectionCard>

            <SectionCard
              title="Budget and schedule"
              subtitle="What it spends, and when"
              icon={Wallet}
              collapsible={false}
            >
              <div className="grid grid-cols-12 gap-4 p-5">
                <div className="col-span-12 sm:col-span-6">
                  <Label>Budget type</Label>
                  <Select
                    className="mt-1 bg-muted/30"
                    options={BUDGET_KINDS}
                    value={form.budgetKind}
                    onChange={(v) => set('budgetKind', v)}
                    searchable={false}
                  />
                </div>
                <div className="col-span-12 sm:col-span-6">
                  <Label htmlFor="campaign-budget">
                    Amount {currency ? `(${currency})` : ''}
                  </Label>
                  <Input
                    id="campaign-budget"
                    className="mt-1"
                    type="number"
                    min={1}
                    value={form.budget}
                    onChange={(e) => set('budget', e.target.value)}
                  />
                </div>
                <div className="col-span-12 sm:col-span-6">
                  <Label htmlFor="campaign-start">Starts</Label>
                  <Input
                    id="campaign-start"
                    className="mt-1"
                    type="datetime-local"
                    value={form.startAt}
                    onChange={(e) => set('startAt', e.target.value)}
                  />
                </div>
                <div className="col-span-12 sm:col-span-6">
                  <Label htmlFor="campaign-end">
                    Ends {form.budgetKind === 'daily' ? '(optional)' : ''}
                  </Label>
                  <Input
                    id="campaign-end"
                    className="mt-1"
                    type="datetime-local"
                    value={form.endAt}
                    onChange={(e) => set('endAt', e.target.value)}
                  />
                </div>
              </div>
            </SectionCard>
          </div>

          {/* Preview */}
          <Card className="flex h-fit flex-col overflow-hidden p-0 lg:sticky lg:top-6">
            <div className="border-b border-[hsl(var(--hairline))] px-5 py-4">
              <div className="mono-label text-muted-foreground">Preview</div>
            </div>
            <div className="p-5">
              {adCount !== null && (
                <div className="mb-4 font-mono text-[10px] text-muted-foreground">
                  {adCount === 1 ? '1 ad' : `${adCount} ads`} ·{' '}
                  {form.listingSource === 'feed'
                    ? 'follows the feed'
                    : 'fixed set'}
                </div>
              )}
              {form.kind !== 'listings' ? (
                // No creative pipeline for the proptree kinds yet. Say
                // what the campaign would contain rather than showing a
                // listing ad that isn't what this campaign promotes.
                <div className="rounded-xl border border-dashed border-border p-8 text-center">
                  <p className="font-sans text-[13px] text-muted-foreground">
                    {KIND_HINT[form.kind]}
                  </p>
                  <p className="mt-2 font-mono text-[10px] text-muted-foreground">
                    No preview yet — the creative for this type isn't built.
                  </p>
                </div>
              ) : previewListing ? (
                <div className="flex justify-center overflow-hidden rounded-xl border border-border">
                  <ListingBanner
                    // `width` is not optional in practice: without it the
                    // banner renders at its native size and overflows the
                    // column instead of scaling to it.
                    width={PREVIEW_WIDTH}
                    ratio="1:1"
                    variant="social"
                    // Agency brand kit isn't wired here yet — the preview
                    // exists to show which listing and shape the ad
                    // carries, not to be the final creative.
                    brand={DEFAULT_BRAND}
                    listing={bannerListingFrom(previewListing)}
                  />
                </div>
              ) : (
                <div className="rounded-xl border border-dashed border-border p-8 text-center font-mono text-[11px] text-muted-foreground">
                  {form.listingSource === 'feed'
                    ? 'Pick a feed to preview its creative.'
                    : 'Pick a listing to preview the creative.'}
                </div>
              )}
            </div>
            <div className="space-y-3 border-t border-[hsl(var(--hairline))] px-5 py-4">
              {problems.length > 0 && (
                <ul className="space-y-1">
                  {problems.map((p) => (
                    <li
                      key={p}
                      className="font-mono text-[10px] leading-relaxed text-muted-foreground"
                    >
                      {p}
                    </li>
                  ))}
                </ul>
              )}
              {/* Nothing to submit to yet: creating a campaign means
                  campaign → ad set → creative → ad on the Marketing API,
                  none of which exists. Disabled rather than hidden so the
                  end of the flow is visible while it's being built. */}
              <div className="flex items-center justify-between gap-3">
                <span className="font-mono text-[10px] text-muted-foreground">
                  {housing ? 'Special ad category: Housing' : 'No special ad category'}
                </span>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => navigate('/marketing')}
                  >
                    Cancel
                  </Button>
                  <Button size="sm" disabled title="Not wired to Meta yet">
                    Launch
                  </Button>
                </div>
              </div>
            </div>
          </Card>
        </div>
      </div>
    </AppShell>
  );
}
